import { useAuth } from "@/context/AuthContext";
import { bulkDeleteDocuments, type BulkDeleteResponse } from "@/services/documentsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchAuthSession } from "aws-amplify/auth";
import { useLayoutEffect, useRef } from "react";

/**
 * Hook to get access token for API calls
 */
async function getAccessToken(): Promise<string> {
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();
  if (!accessToken) {
    throw new Error("No access token available");
  }
  return accessToken;
}

export interface UseBulkDeleteOptions {
  /** Callback when deletion is successful */
  onSuccess?: (response: BulkDeleteResponse) => void;
  /** Callback when deletion fails */
  onError?: (error: Error) => void;
}

export interface UseBulkDeleteReturn {
  /** Execute bulk delete */
  bulkDelete: (documentIds: number[], hardDelete: boolean) => void;
  /** Whether delete is in progress */
  isDeleting: boolean;
  /** Error from last delete attempt */
  error: Error | null;
  /** Response from last successful delete */
  lastResponse: BulkDeleteResponse | null;
}

/**
 * Hook to handle bulk document deletion with TanStack Query
 * Invalidates documents and metrics queries on success
 */
export function useBulkDelete(options: UseBulkDeleteOptions = {}): UseBulkDeleteReturn {
  const { onSuccess, onError } = options;
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Refs to hold latest callbacks - avoids stale closure issues in mutation handlers
  const onSuccessRef = useRef<typeof onSuccess>(undefined);
  const onErrorRef = useRef<typeof onError>(undefined);
  useLayoutEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  });

  const mutation = useMutation({
    mutationFn: async ({
      documentIds,
      hardDelete,
    }: {
      documentIds: number[];
      hardDelete: boolean;
    }) => {
      if (!isAuthenticated) {
        throw new Error("Not authenticated");
      }
      const token = await getAccessToken();
      return bulkDeleteDocuments(documentIds, token, hardDelete);
    },
    onSuccess: (response) => {
      // Invalidate documents and metrics queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["metrics"] });

      if (onSuccessRef.current) {
        onSuccessRef.current(response);
      }
    },
    onError: (error: Error) => {
      if (onErrorRef.current) {
        onErrorRef.current(error);
      }
    },
  });

  const bulkDelete = (documentIds: number[], hardDelete: boolean) => {
    mutation.mutate({ documentIds, hardDelete });
  };

  return {
    bulkDelete,
    isDeleting: mutation.isPending,
    error: mutation.error,
    lastResponse: mutation.data ?? null,
  };
}
